/**
 * Blog and article pages (sections/main-blog.liquid, sections/main-article.liquid), progressive enhancement:
 * - « Afficher plus d'articles » appends the next page's cards instead of opening it;
 * - the table of contents marks the heading being read (`aria-current`), the mobile one closes on a click;
 * - « Copier le lien » copies the article address (the button only shows with JS).
 */

/**
 * Moves the cards of a fetched blog page into `grid`; returns the next page's address, or null at the end.
 *
 * @param {string} html - The fetched page.
 * @param {Element} grid - The list to append to.
 * @returns {string | null}
 */
export function appendPage(html, grid) {
  const page = new DOMParser().parseFromString(html, 'text/html');
  for (const li of page.querySelectorAll('[data-bl-grid] > li')) grid.append(document.adoptNode(li));
  return page.querySelector('[data-bl-more]')?.getAttribute('href') ?? null;
}

/**
 * The heading being read: the last one whose top has passed `offset` (px from the top of the window), else
 * the first.
 *
 * @param {{ id: string, top: number }[]} headings - In document order, `top` relative to the window.
 * @param {number} offset
 */
export function currentHeading(headings, offset) {
  let id = headings[0]?.id ?? '';
  for (const h of headings) if (h.top <= offset) id = h.id;
  return id;
}

/** @param {HTMLAnchorElement} more */
function loadMore(more) {
  const grid = document.querySelector('[data-bl-grid]');
  more.addEventListener('click', async (event) => {
    if (!grid || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    more.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(more.href);
      if (!response.ok) throw new Error(String(response.status));
      const next = appendPage(await response.text(), grid);
      if (next) more.href = next;
      else more.parentElement?.remove();
    } catch {
      location.href = more.href;
    } finally {
      more.removeAttribute('aria-busy');
    }
  });
}

/** @param {HTMLElement} toc */
function spy(toc) {
  const links = /** @type {HTMLAnchorElement[]} */ ([...toc.querySelectorAll('a[href^="#"]')]);
  const headings = links
    .map((a) => document.getElementById(decodeURIComponent(a.hash.slice(1))))
    .filter((h) => h !== null);
  let raf = 0;
  const update = () => {
    raf = 0;
    const id = currentHeading(
      headings.map((h) => ({ id: h.id, top: h.getBoundingClientRect().top })),
      140,
    );
    for (const a of links) {
      if (a.hash === `#${id}`) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    }
  };
  addEventListener('scroll', () => (raf ||= requestAnimationFrame(update)), { passive: true });
  update();
}

const more = document.querySelector('[data-bl-more]');
if (more instanceof HTMLAnchorElement) loadMore(more);

const toc = document.querySelector('[data-ar-toc]');
if (toc instanceof HTMLElement) spy(toc);

for (const details of document.querySelectorAll('.ar-tocm')) {
  details.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('a')) details.removeAttribute('open');
  });
}

for (const button of document.querySelectorAll('[data-ar-copy]')) {
  if (!(button instanceof HTMLButtonElement) || !navigator.clipboard) continue;
  button.hidden = false;
  button.addEventListener('click', async () => {
    await navigator.clipboard.writeText(button.dataset.arCopy ?? location.href);
    const status = button.parentElement?.querySelector('.ar-copied');
    if (status) status.textContent = button.dataset.copied ?? '';
  });
}
